const isPerformanceTrackingEnabled = () => (
  process.env.NODE_ENV === 'development' &&
  typeof window !== 'undefined' &&
  typeof performance !== 'undefined'
);

const createDurationBucket = () => ({
  count: 0,
  totalMs: 0,
  averageMs: 0,
  maxMs: 0,
  minMs: null,
  lastMs: 0,
  lastMeta: null,
});

const createCounterBucket = () => ({
  count: 0,
  lastMeta: null,
});

const cloneDurations = (durations) => (
  Object.fromEntries(
    Object.entries(durations).map(([key, value]) => [key, { ...value }])
  )
);

const cloneCounters = (counters) => (
  Object.fromEntries(
    Object.entries(counters).map(([key, value]) => [key, { ...value }])
  )
);

const createDisabledTracker = () => ({
  enabled: false,
  count: () => 0,
  sample: (_name, value) => value,
  start: () => null,
  end: () => null,
  measure: (_name, fn) => fn(),
  reset: () => { },
  snapshot: () => ({ counters: {}, durations: {} }),
  logSummary: () => { },
});

export const createCalendarPerformanceTracker = (scope = 'calendar') => {
  if (!isPerformanceTrackingEnabled()) {
    return createDisabledTracker();
  }

  const counters = {};
  const durations = {};

  const ensureCounter = (name) => {
    if (!counters[name]) {
      counters[name] = createCounterBucket();
    }
    return counters[name];
  };

  const ensureDuration = (name) => {
    if (!durations[name]) {
      durations[name] = createDurationBucket();
    }
    return durations[name];
  };

  const snapshot = () => ({
    counters: cloneCounters(counters),
    durations: cloneDurations(durations),
  });

  const count = (name, amount = 1, meta = null) => {
    const bucket = ensureCounter(name);
    bucket.count += amount;
    bucket.lastMeta = meta;
    return bucket.count;
  };

  const sample = (name, value, meta = null) => {
    if (!Number.isFinite(value)) {
      return value;
    }

    const bucket = ensureDuration(name);
    bucket.count += 1;
    bucket.totalMs += value;
    bucket.averageMs = bucket.totalMs / bucket.count;
    bucket.maxMs = Math.max(bucket.maxMs, value);
    bucket.minMs = bucket.minMs === null ? value : Math.min(bucket.minMs, value);
    bucket.lastMs = value;
    bucket.lastMeta = meta;
    return value;
  };

  const start = (name, meta = null) => ({
    name,
    meta,
    startedAt: performance.now(),
  });

  const end = (token, meta = null) => {
    if (!token) {
      return null;
    }

    const duration = performance.now() - token.startedAt;
    sample(token.name, duration, {
      ...(token.meta || {}),
      ...(meta || {}),
    });
    return duration;
  };

  const measure = (name, fn, meta = null) => {
    const token = start(name, meta);
    try {
      return fn();
    } finally {
      end(token);
    }
  };

  const reset = () => {
    Object.keys(counters).forEach((key) => delete counters[key]);
    Object.keys(durations).forEach((key) => delete durations[key]);
  };

  const logSummary = () => {
    const currentSnapshot = snapshot();
    console.groupCollapsed(`[CALENDAR PERF] ${scope}`);
    if (Object.keys(currentSnapshot.counters).length > 0) {
      console.table(currentSnapshot.counters);
    }
    if (Object.keys(currentSnapshot.durations).length > 0) {
      console.table(currentSnapshot.durations);
    }
    console.groupEnd();
    return currentSnapshot;
  };

  const tracker = {
    enabled: true,
    count,
    sample,
    start,
    end,
    measure,
    reset,
    snapshot,
    logSummary,
  };

  window.__calendarPerf = window.__calendarPerf || {};
  window.__calendarPerf[scope] = {
    get counters() {
      return cloneCounters(counters);
    },
    get durations() {
      return cloneDurations(durations);
    },
    snapshot,
    logSummary,
    reset,
  };

  return tracker;
};
